/**
 * Why the section comparison is missing, in the operator's words (§0at).
 *
 * The belt hides a card with nothing to send — that rule is right and stays.
 * But "nothing to send" and "we could not look" are different states wearing
 * the same blank space, and the second one was costing the product its worst
 * demo moment: `/free-tools/convert-pdf-to-word` carries the largest demand
 * gap on its domain (9,059) and shows one internal link, because adobe.com
 * refused our crawler. The screen said the tool found nothing. The truth is
 * the tool was blocked, which is a fact about adobe rather than about the
 * page, and an operator who knows it stops trying to fix the page.
 *
 * Measured 2026-09-20: **61 of 782** coverage snapshots are
 * `rival_crawl_failed`, and they cluster on the tool and template pages where
 * the biggest gaps sit — because those pages' rivals are the sites most likely
 * to block a crawler (adobe.com, docs.google.com). The single largest gap on
 * each pilot domain is in this state.
 *
 * Deliberately silent on `ready` and `skipped`. A comparison that ran and
 * found nothing is the hide-the-card case, and saying "no gaps found" there
 * would put a reassurance on a surface that exists to hand off work.
 */
export type SectionCompareStatus = 'ready' | 'rival_crawl_failed' | 'eval_unavailable' | 'skipped';

/**
 * Did this comparison fail, rather than run? (TECHNICAL_DEBT §0aj, §0au.)
 *
 * The eval never throws: a sidecar that is down, times out or crashes on the pair leaves a row
 * with `error` set, no sections and `missingSectionCount: 0`, which is exactly the shape of a
 * comparison that ran and found nothing. Before `eval_unavailable` existed, and still whenever the
 * health probe answered but the comparison itself failed, that row was stored as `ready`. The
 * error is the only thing telling the two apart, so this is the one place that reads it.
 */
export function comparisonFailed(evalRow: { error?: unknown } | Record<string, unknown> | null | undefined): boolean {
  const error = (evalRow as { error?: unknown } | null | undefined)?.error;
  return typeof error === 'string' ? error.trim().length > 0 : Boolean(error);
}

/**
 * A stored coverage row's status as it should be read: `ready` with a failed comparison inside is
 * `eval_unavailable`. Applied on read so rows written before the store checked it read honestly too.
 */
export function effectiveSectionCompareStatus(
  coverage: { status?: SectionCompareStatus | string | null; evalRow?: unknown } | null | undefined
): SectionCompareStatus | null {
  if (!coverage?.status) return null;
  const status = coverage.status as SectionCompareStatus;
  if (status === 'ready' && comparisonFailed(coverage.evalRow as Record<string, unknown> | null)) {
    return 'eval_unavailable';
  }
  return status;
}

export interface SectionCompareNotice {
  text: string;
  /**
   * `unreadable` is about their page (we hold no copy of it); `unavailable` is about our side (we
   * hold both pages and the comparison failed). Different causes, so different sentences.
   */
  kind: 'unreadable' | 'unavailable';
}

/** Strip protocol and `www.` — the operator reads a site, not a URL. */
function siteName(domain: string | null | undefined): string {
  return String(domain || '')
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/.*$/, '');
}

export function sectionCompareNotice(
  status: SectionCompareStatus | null | undefined,
  rivalDomain?: string | null
): SectionCompareNotice | null {
  const site = siteName(rivalDomain);
  switch (status) {
    case 'rival_crawl_failed':
      // Names the site, because the next move depends on whose page it was: a one-off failure is
      // worth retrying and adobe.com is not.
      //
      // It does not say why. The state covers a page that answered with an error (403, 404) and a
      // page whose crawl stored nothing at all; on the sprint 6 queues 6 of the 20 unreadable rivals
      // had a stored 403 and 14 had no stored response, so "their site blocked our crawler" was a
      // guess on most of them. Worded apart from the failed comparison below (that one is our side,
      // with both pages in hand) and from an empty card (what is missing is unknown, not nothing).
      return {
        kind: 'unreadable',
        text: site
          ? `We could not read the ${site} page this one is compared with, so no sections were compared: what this page is missing is unknown, not nothing.`
          : 'We could not read the search result this page is compared with, so no sections were compared: what this page is missing is unknown, not nothing.'
      };
    case 'eval_unavailable':
      // Ours, not theirs (§0aj, §0au). It used to end "It will be retried", which
      // nothing did: cost-performance-index carried it from 1 Oct through a
      // full recrawl (findings §28). The build now retries before giving up, so
      // a row that reaches here has already been retried; say only what is true.
      //
      // Worded apart from the blocked notice above (that one is about reading THEIR site; this is
      // about comparing THIS page) and from an empty card (no result is not a clean result).
      // It no longer says "we read <site>": a row can fail before the rival's body is in hand.
      return {
        kind: 'unavailable',
        text: site
          ? `We could not compare this page with ${site}: the comparison failed on our side, so this is not a finding that nothing is missing.`
          : 'We could not compare this page with the top result: the comparison failed on our side, so this is not a finding that nothing is missing.'
      };
    default:
      return null;
  }
}
